import { Injectable } from "@angular/core";
import { CacheFactory, CacheStore, fromMillisecondsToMinutes, StorageMode } from "@obg/ngx.global";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { CoreHttpClient } from "../http-client/http-client.service";

import { ResponsibleGamingConfigService } from "./responsible-gaming.config";
import { InstantSelfExclusionRequest, SelfExclusionRequest } from "./responsible-gaming.model";

const CACHE_KEY = "common.responsible-gaming";
const SESSION_LIMIT_TIMER_START_KEY = "sessionLimitTimerStartDate";
const SESSION_DURATION_KEY = "sessionDuration";

/**
 * @deprecated [fabric-deprecated] - Use ResponsibleGamingService from app-responsible-gaming instead
 */
@Injectable({
	providedIn: "root",
})
export class ResponsibleGamingService {
	private cache: CacheStore;

	constructor(
		private httpClient: CoreHttpClient,
		private config: ResponsibleGamingConfigService,
		private cacheFactory: CacheFactory
	) {
		this.cache = this.cacheFactory.get(CACHE_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: Number.MAX_VALUE,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateSelfExclusionPeriod instead
	 */
	updateSelfExclusionPeriod(data: SelfExclusionRequest): Observable<object> {
		return this.httpClient.put({
			url: this.config.getSelfExclusionEndpoint(),
			data,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateInstantSelfExclusionPeriod instead
	 */
	updateInstantSelfExclusionPeriod(data: InstantSelfExclusionRequest): Observable<object> {
		return this.httpClient.put({
			url: this.config.getInstantSelfExclusionEndpoint(),
			data,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.UpdateCoolOffPeriod instead
	 */
	updateCoolOffPeriod(data: SelfExclusionRequest): Observable<object> {
		return this.httpClient.put({
			url: this.config.getCoolOffPeriodEndpoint(),
			data,
		});
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.StoreSessionLimitTimerStartDate instead
	 *
	 * Stores timer start time so timer may continue when user gets back from 3rd party site
	 */
	storeSessionLimitTimerStartDate(startTime: Date): Promise<number> {
		return this.cache.setItem(SESSION_LIMIT_TIMER_START_KEY, startTime.getTime());
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetSessionLimitTimerLeftTime instead
	 *
	 * Gets ms left till session limit expiration.
	 * Used on verify when user gets back from 3rd party site.
	 * User logs in [timer starts] -> goes to provider site to play -> gets back (verify) [timer continues]
	 */
	getSessionLimitTimerLeftTime(checkPeriod: number): Observable<number> {
		if (checkPeriod <= 0) {
			return of(0);
		}

		return this.cache.getItemAsObservable<number>(SESSION_LIMIT_TIMER_START_KEY).pipe(
			map((startTime) => {
				if (!startTime) {
					return checkPeriod;
				}

				const timePassed = new Date().getTime() - startTime;
				const timeLeft = checkPeriod - timePassed;
				return timeLeft > 0 ? timeLeft : 1; // 1 ms for not being filtered out as not set
			})
		);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.StoreSessionDuration instead
	 */
	storeSessionDuration(sessionDurationMinutes: number): Promise<number> {
		return this.cache.setItem(SESSION_DURATION_KEY, sessionDurationMinutes);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetNewSessionDuration instead
	 *
	 * Increments current session duration (from storage) by check period
	 * @return in minutes
	 */
	getNewSessionDuration(checkPeriod: number): Observable<number> {
		return this.cache
			.getItemAsObservable<number>(SESSION_DURATION_KEY)
			.pipe(map((currentDuration) => (currentDuration || 0) + fromMillisecondsToMinutes(checkPeriod)));
	}
}
